import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UserRound } from "lucide-react";

export default function HostProfileEditor() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", bio: "", location: "" });
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({ name: user.name || "", bio: user.bio || "", location: user.location || "" });
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/host/profile", form);
      toast.success("Profile updated");
      setOpen(false);
      refresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-subtle rounded-2xl p-6" data-testid="host-profile-editor">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-moss text-canvas flex items-center justify-center font-serif text-2xl shrink-0">
            <UserRound className="h-6 w-6" strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <div className="overline">About you</div>
            <div className="font-serif text-xl mt-0.5">{form.name || "—"}</div>
            {form.location && <div className="text-xs text-muted2">{form.location}</div>}
            <p className="text-sm text-muted2 mt-2 line-clamp-3 whitespace-pre-line">
              {form.bio || "Add a bio so travelers know who they're staying with — where you're from, what you love, why you host."}
            </p>
          </div>
        </div>
        <Button data-testid="edit-host-profile" onClick={() => setOpen((o) => !o)} variant="outline" className="rounded-full bg-white border-subtle hover:border-moss shrink-0">
          {open ? "Cancel" : "Edit"}
        </Button>
      </div>

      {open && (
        <div className="mt-5 grid gap-3 pt-5 border-t border-subtle">
          <div>
            <Label className="text-xs">Display name</Label>
            <Input data-testid="host-profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg bg-white border-subtle" />
          </div>
          <div>
            <Label className="text-xs">Where you're based</Label>
            <Input data-testid="host-profile-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Rishikesh, India" className="rounded-lg bg-white border-subtle" />
          </div>
          <div>
            <Label className="text-xs">Your story (max 1000 chars)</Label>
            <Textarea
              data-testid="host-profile-bio"
              rows={5}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value.slice(0, 1000) })}
              placeholder="Tell travelers about yourself — what you host, why you host, what makes your space special."
              className="rounded-lg bg-white border-subtle"
            />
            <div className="text-[10px] text-muted2 mt-1 text-right">{form.bio.length}/1000</div>
          </div>
          <Button data-testid="save-host-profile" onClick={save} disabled={saving} className="rounded-full bg-moss hover:bg-moss-hover text-canvas">
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
}
